/**
 * Builds the array of Timeline objects from events and optional server timelines.
 *
 * Extracted to avoid duplicating the buildTimeline + convertServerTimeline
 * memoization in every call site that needs the timelines array before
 * `useTranscriptTimeline` builds it internally.
 */

import { useMemo } from "react";

import type {
  Event,
  Timeline as ServerTimeline,
} from "@tsmono/inspect-common/types";

import type { Timeline } from "../core";
import {
  buildTimeline,
  convertServerTimeline,
  filterEmptyBranches,
  TimelineEvent,
  TimelineSpan,
} from "../core";

// =============================================================================
// Orphaned-event helpers
// =============================================================================

/** Minimal span info extracted from span_begin events. */
interface SpanInfo {
  id: string;
  name: string;
  type?: string | null;
  parentId?: string | null;
  beginEvent: Event;
  endEvent?: Event;
}

/**
 * Collect all event UUIDs and span IDs referenced within a timeline's tree.
 */
function collectReferencedIds(
  items: (TimelineEvent | TimelineSpan)[],
  uuids: Set<string>,
  spanIds: Set<string>
): void {
  for (const item of items) {
    if (item.type === "event") {
      const uuid = (item.event as { uuid?: string | null }).uuid;
      if (uuid) uuids.add(uuid);
    } else {
      spanIds.add(item.id);
      collectReferencedIds(item.content, uuids, spanIds);
      collectReferencedIds(item.branches, uuids, spanIds);
    }
  }
}

/**
 * Build a lookup of span_begin events and collect span_end events.
 */
function buildSpanLookup(events: Event[]): Map<string, SpanInfo> {
  const spans = new Map<string, SpanInfo>();
  // First pass: register all span_begin events
  for (const event of events) {
    if (event.event === "span_begin") {
      spans.set(event.id, {
        id: event.id,
        name: event.name,
        type: event.type,
        parentId: event.parent_id,
        beginEvent: event,
      });
    }
  }
  // Second pass: attach span_end events
  for (const event of events) {
    if (event.event === "span_end") {
      const span = spans.get(event.id);
      if (span) {
        span.endEvent = event;
      }
    }
  }
  return spans;
}

/**
 * Build orphan content items, preserving the minimal span tree needed to
 * house orphaned events.
 *
 * Walks the orphaned events, groups them by their direct parent span, and
 * recursively builds only the ancestor spans that contain at least one orphan.
 * Spans that are already represented in a server timeline are not recreated —
 * their orphaned children are promoted to the level above.
 */
function buildOrphanContent(
  orphanEvents: Event[],
  spanLookup: Map<string, SpanInfo>,
  referencedSpanIds: Set<string>
): (TimelineEvent | TimelineSpan)[] {
  // Group orphan events by their direct parent span (or null for root-level)
  const bySpan = new Map<string | null, Event[]>();
  for (const event of orphanEvents) {
    const spanId = (event as { span_id?: string | null }).span_id ?? null;
    // If the span is already in a timeline, treat this event as root-level
    const effectiveSpanId =
      spanId && !referencedSpanIds.has(spanId) && spanLookup.has(spanId)
        ? spanId
        : null;
    let group = bySpan.get(effectiveSpanId);
    if (!group) {
      group = [];
      bySpan.set(effectiveSpanId, group);
    }
    group.push(event);
  }

  // Collect the set of span IDs that directly contain orphans
  const neededSpanIds = new Set<string>();
  for (const spanId of bySpan.keys()) {
    if (spanId !== null) {
      neededSpanIds.add(spanId);
    }
  }

  // Walk up parent chains to find all ancestor spans needed
  const allNeededSpanIds = new Set(neededSpanIds);
  for (const spanId of neededSpanIds) {
    let current = spanLookup.get(spanId);
    while (current?.parentId) {
      if (referencedSpanIds.has(current.parentId)) break;
      if (!spanLookup.has(current.parentId)) break;
      allNeededSpanIds.add(current.parentId);
      current = spanLookup.get(current.parentId);
    }
  }

  // Build the span tree bottom-up: for each needed span, determine its
  // effective parent (the nearest needed ancestor, or null for root)
  const spanChildren = new Map<
    string | null,
    (TimelineEvent | TimelineSpan)[]
  >();

  // Add orphan events to their parent span (or root)
  for (const [spanId, events] of bySpan) {
    const items = events.map((e) => new TimelineEvent(e));
    let group = spanChildren.get(spanId);
    if (!group) {
      group = [];
      spanChildren.set(spanId, group);
    }
    group.push(...items);
  }

  // Register each needed span under its effective parent
  // Process from leaves to roots by sorting by depth
  const spanDepth = (id: string): number => {
    let depth = 0;
    let current = spanLookup.get(id);
    while (current?.parentId && allNeededSpanIds.has(current.parentId)) {
      depth++;
      current = spanLookup.get(current.parentId);
    }
    return depth;
  };

  const sortedSpanIds = [...allNeededSpanIds].sort(
    (a, b) => spanDepth(b) - spanDepth(a)
  );

  for (const spanId of sortedSpanIds) {
    const info = spanLookup.get(spanId)!;
    const children = spanChildren.get(spanId) ?? [];

    // Sort children by start time
    children.sort((a, b) => a.startTime().getTime() - b.startTime().getTime());

    if (children.length === 0) continue;

    const span = new TimelineSpan({
      id: info.id,
      name: info.name,
      spanType: info.type ?? null,
      content: children,
      utility: false,
    });

    // Find effective parent: nearest needed ancestor, or null for root
    let parentId: string | null = null;
    let current = info;
    while (current.parentId) {
      if (allNeededSpanIds.has(current.parentId)) {
        parentId = current.parentId;
        break;
      }
      current = spanLookup.get(current.parentId) ?? current;
      if (current === info) break; // safety: prevent infinite loop
    }

    let parentGroup = spanChildren.get(parentId);
    if (!parentGroup) {
      parentGroup = [];
      spanChildren.set(parentId, parentGroup);
    }
    parentGroup.push(span);
  }

  // Root-level items
  const rootItems = spanChildren.get(null) ?? [];
  rootItems.sort((a, b) => a.startTime().getTime() - b.startTime().getTime());
  return rootItems;
}

/**
 * Keep orphaned events visible without distorting server-authored timelines.
 *
 * When server-provided timelines don't reference every event (e.g. scorer
 * events), a single timeline receives the orphan set in chronological order.
 * With multiple timelines, the full built timeline is appended as an Overall
 * view so no authored timeline receives another timeline's events.
 */
function attachOrphanedEvents(
  timelines: Timeline[],
  events: Event[],
  builtTimeline: Timeline
): Timeline[] {
  if (timelines.length === 0 || events.length === 0) return timelines;

  // Collect all UUIDs and span IDs already referenced across every timeline
  const referencedUuids = new Set<string>();
  const referencedSpanIds = new Set<string>();
  for (const tl of timelines) {
    collectReferencedIds(tl.root.content, referencedUuids, referencedSpanIds);
    collectReferencedIds(tl.root.branches, referencedUuids, referencedSpanIds);
    referencedSpanIds.add(tl.root.id);
  }

  // Find orphaned events: have a UUID, not referenced, not span bookkeeping
  const orphanEvents: Event[] = [];
  for (const event of events) {
    if (event.event === "span_begin" || event.event === "span_end") continue;
    const uuid = (event as { uuid?: string | null }).uuid;
    if (uuid && !referencedUuids.has(uuid)) {
      orphanEvents.push(event);
    }
  }

  if (orphanEvents.length === 0) return timelines;

  if (timelines.length > 1) {
    return [
      ...timelines,
      {
        name: "Overall",
        description: "Full sample transcript",
        root: builtTimeline.root,
      },
    ];
  }

  // Build span lookup for parent-chain resolution
  const spanLookup = buildSpanLookup(events);

  // Build orphan content with minimal span structure
  const orphanContent = buildOrphanContent(
    orphanEvents,
    spanLookup,
    referencedSpanIds
  );

  if (orphanContent.length === 0) return timelines;

  // Merge into the first timeline whose root isn't a branch tree (orphans
  // have no natural position in a branch tree), falling back to the first.
  const hostIndex = Math.max(
    0,
    timelines.findIndex((tl) => tl.root.branches.length === 0)
  );
  const host = timelines[hostIndex]!;
  const mergedContent = [...host.root.content, ...orphanContent];
  mergedContent.sort(
    (a, b) => a.startTime().getTime() - b.startTime().getTime()
  );

  const newRoot = new TimelineSpan({
    id: host.root.id,
    name: host.root.name,
    spanType: host.root.spanType,
    content: mergedContent,
    branches: host.root.branches,
    branchedFrom: host.root.branchedFrom,
    description: host.root.description,
    utility: host.root.utility,
    agentResult: host.root.agentResult,
    outline: host.root.outline,
  });

  return timelines.map((tl, i) =>
    i === hostIndex
      ? { name: host.name, description: host.description, root: newRoot }
      : tl
  );
}

// =============================================================================
// Hook
// =============================================================================

export interface UseTimelinesArrayOptions {
  /** Show branches whose content is only structural events. Default: false. */
  showEmptyBranches?: boolean;
}

export function useTimelinesArray(
  events: Event[],
  serverTimelines?: ServerTimeline[],
  options?: UseTimelinesArrayOptions
): Timeline[] {
  const showEmptyBranches = options?.showEmptyBranches ?? false;

  const builtTimeline = useMemo(() => buildTimeline(events), [events]);
  const convertedTimelines = useMemo(
    () =>
      serverTimelines && serverTimelines.length > 0
        ? serverTimelines.map((tl) => convertServerTimeline(tl, events))
        : null,
    [serverTimelines, events]
  );
  const withOrphans = useMemo(
    () =>
      convertedTimelines
        ? attachOrphanedEvents(convertedTimelines, events, builtTimeline)
        : null,
    [convertedTimelines, events, builtTimeline]
  );
  const resolved = useMemo(
    () => withOrphans ?? [builtTimeline],
    [withOrphans, builtTimeline]
  );
  return useMemo(
    () => (showEmptyBranches ? resolved : resolved.map(filterEmptyBranches)),
    [resolved, showEmptyBranches]
  );
}
